import React, { useState, useEffect, useCallback, useRef, RefObject, useMemo } from 'react'
import { AiTextTransformContextMenu } from './ai-text-transform-context-menu'
import { AiTextTransformPopover } from './ai-text-transform-popover'
import { AiTextTransformButton } from './ai-text-transform-button'
import lang from '@/lang'
import { useAppSelector } from '@/redux/store'
import { selectAiSettingsEnabled } from '@/redux/ducks/tenantUser/settings'

interface SelectionPosition {
  top: number
  left: number
  width: number
  height: number
}

interface UseAiTextTransformOptions {
  containerRef: RefObject<HTMLElement>
  enabled?: boolean
  onApplyResult: (result: string, originalText: string, optionId: string) => void
  inputRef?: RefObject<HTMLTextAreaElement | HTMLInputElement>
}

interface UseAiTextTransformResult {
  renderAiComponents: () => React.ReactNode
}

const ICON_OFFSET = 8
const ICON_HEIGHT = 32
const ICON_WIDTH = 32

export const useAiTextTransform = ({
  containerRef,
  inputRef,
  enabled = true,
  onApplyResult,
}: UseAiTextTransformOptions): UseAiTextTransformResult => {
  const [selectedText, setSelectedText] = useState('')
  const [selectionPosition, setSelectionPosition] = useState<SelectionPosition | null>(null)
  const [showIcon, setShowIcon] = useState(false)
  const savedSelectionRef = useRef<string>('')
  const iconButtonRef = useRef<HTMLElement | null>(null)
  const globalAiEnabled = useAppSelector(selectAiSettingsEnabled)

  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLElement | null>(null)
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [selectedOption, setSelectedOption] = useState<{ id: string; label: string } | null>(null)

  const aiEnabled = useMemo(() => enabled && globalAiEnabled, [enabled, globalAiEnabled])

  const hasOpenMenus = useMemo(
    () => Boolean(contextMenuAnchor || popoverAnchor),
    [contextMenuAnchor, popoverAnchor],
  )

  const clearSelection = useCallback(() => {
    setSelectedText('')
    setSelectionPosition(null)
    setShowIcon(false)
    setContextMenuAnchor(null)
    setPopoverAnchor(null)
    setSelectedOption(null)
    savedSelectionRef.current = ''
    iconButtonRef.current = null
  }, [])

  const getInputSelectionData = useCallback((
    input: HTMLTextAreaElement | HTMLInputElement,
  ): { text: string; position: SelectionPosition } | null => {
    const start = input.selectionStart || 0
    const end = input.selectionEnd || 0

    if (start === end) return null

    const selectedTextContent = input.value.substring(start, end).trim()
    if (selectedTextContent.length === 0) return null

    const inputRect = input.getBoundingClientRect()
    const containerRect = containerRef.current?.getBoundingClientRect()

    if (!containerRect) return null

    // Check if there's enough space on the right side
    const spaceOnRight = window.innerWidth - inputRect.right
    const needsSpace = ICON_WIDTH + ICON_OFFSET
    const shouldPositionLeft = spaceOnRight < needsSpace

    const leftPosition = shouldPositionLeft
      ? inputRect.left - containerRect.left - ICON_WIDTH - ICON_OFFSET
      : inputRect.right - containerRect.left + ICON_OFFSET

    const position: SelectionPosition = {
      top: inputRect.top - containerRect.top + (inputRect.height / 2) - (ICON_HEIGHT / 2),
      left: leftPosition,
      width: inputRect.width,
      height: inputRect.height,
    }

    return { text: selectedTextContent, position }
  }, [containerRef])

  const getContentEditableSelectionData = useCallback((): { text: string; position: SelectionPosition } | null => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return null

    const range = selection.getRangeAt(0)

    if (!containerRef.current?.contains(range.commonAncestorContainer)) {
      return null
    }

    const plainTextSelection = selection.toString().trim()
    if (plainTextSelection.length === 0) return null

    const clonedContent = range.cloneContents()
    const tempDiv = document.createElement('div')
    tempDiv.appendChild(clonedContent)
    let selectedTextContent = tempDiv.innerHTML.trim()

    if (selectedTextContent.length === 0) {
      selectedTextContent = plainTextSelection
    }

    if (selectedTextContent.length === 0) return null

    const rects = range.getBoundingClientRect()
    if (rects.width === 0 || rects.height === 0) return null

    const position: SelectionPosition = {
      top: rects.top + (rects.height / 2) - (ICON_HEIGHT / 2),
      left: rects.right + ICON_OFFSET,
      width: rects.width,
      height: rects.height,
    }

    return { text: selectedTextContent, position }
  }, [])

  const updateIconPosition = useCallback(() => {
    if (!aiEnabled || !containerRef.current) return

    const isInputElement = Boolean(inputRef?.current)
    const selectionData = isInputElement
      ? getInputSelectionData(inputRef.current!)
      : getContentEditableSelectionData()

    if (selectionData) {
      setSelectionPosition(selectionData.position)
    }
  }, [aiEnabled, containerRef, inputRef, getInputSelectionData, getContentEditableSelectionData])

  const handleSelectionChange = useCallback(() => {
    if (!aiEnabled || !containerRef.current) return
    if (hasOpenMenus) return

    const isInputElement = Boolean(inputRef?.current)
    const selectionData = isInputElement
      ? getInputSelectionData(inputRef.current!)
      : getContentEditableSelectionData()

    if (!selectionData) {
      if (!hasOpenMenus) {
        clearSelection()
      }
      return
    }

    setSelectedText(selectionData.text)
    setSelectionPosition(selectionData.position)
    setShowIcon(true)
    savedSelectionRef.current = selectionData.text
  }, [
    aiEnabled,
    containerRef,
    inputRef,
    hasOpenMenus,
    getInputSelectionData,
    getContentEditableSelectionData,
    clearSelection,
  ])

  useEffect(() => {
    if (!aiEnabled) {
      clearSelection()
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedElement = event.target as HTMLElement

      if (contextMenuAnchor?.contains(target)) return
      if (popoverAnchor?.contains(target)) return
      if (clickedElement.closest('.MuiMenu-root, .MuiPopover-root, .MuiModal-root, .MuiPopper-root, [data-ai-text-transform-popover]')) return

      if (containerRef.current && !containerRef.current.contains(target)) {
        clearSelection()
      }
    }

    const handleScroll = () => {
      // Update icon position when scrolling if icon is visible
      if (showIcon && !hasOpenMenus) {
        updateIconPosition()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true) // Use capture phase

    // textareas
    if (inputRef?.current) {
      const input = inputRef.current
      const events = ['mouseup', 'select', 'keyup'] as const

      events.forEach((event) => input.addEventListener(event, handleSelectionChange))

      return () => {
        events.forEach((event) => input.removeEventListener(event, handleSelectionChange))
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
      }
    }

    // contenteditable- e.g. html input
    document.addEventListener('selectionchange', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [aiEnabled, inputRef, handleSelectionChange, clearSelection, containerRef, contextMenuAnchor, popoverAnchor, showIcon, hasOpenMenus, updateIconPosition])

  const handleIconClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const textToUse = selectedText || savedSelectionRef.current
      if (!textToUse) return

      if (!selectedText && savedSelectionRef.current) {
        setSelectedText(savedSelectionRef.current)
      }

      iconButtonRef.current = event.currentTarget
      setContextMenuAnchor(event.currentTarget)
    },
    [selectedText],
  )

  const handleContextMenuClose = useCallback(() => {
    setContextMenuAnchor(null)
  }, [])

  const handleOptionSelect = useCallback((optionId: string) => {
    const label = lang.ai.textTransform.popover.titles[optionId] || optionId

    setSelectedOption({ id: optionId, label })
    setPopoverAnchor(iconButtonRef.current)
    setContextMenuAnchor(null)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null)
    setSelectedOption(null)
  }, [])

  const handleApplyResult = useCallback(
    (result: string) => {
      if (!selectedOption) return

      const originalText = selectedText || savedSelectionRef.current
      onApplyResult(result, originalText, selectedOption.id)

      savedSelectionRef.current = ''
      clearSelection()
    },
    [onApplyResult, selectedText, selectedOption, clearSelection],
  )

  const renderAiComponents = useCallback(() => {
    if (!aiEnabled) return null

    const textForPopover = selectedText || savedSelectionRef.current
    const isContextMenuOpen = Boolean(contextMenuAnchor)
    const isPopoverOpen = Boolean(popoverAnchor)
    const positionStrategy = inputRef?.current ? 'absolute' : 'fixed'

    return (
      <>
        {/* Selection Icon */}
        {showIcon && selectionPosition && (
          <AiTextTransformButton
            position={selectionPosition}
            onClick={handleIconClick}
            visible={showIcon}
            positionStrategy={positionStrategy}
          />
        )}

        {/* Context Menu */}
        <AiTextTransformContextMenu
          anchorEl={contextMenuAnchor}
          open={isContextMenuOpen}
          onClose={handleContextMenuClose}
          onSelectOption={handleOptionSelect}
        />

        {/* Popover */}
        {selectedOption && (
          <AiTextTransformPopover
            anchorEl={popoverAnchor}
            open={isPopoverOpen}
            onClose={handlePopoverClose}
            onApply={handleApplyResult}
            selectedText={textForPopover}
            optionLabel={selectedOption.label}
            optionId={selectedOption.id}
          />
        )}
      </>
    )
  }, [
    aiEnabled,
    showIcon,
    selectionPosition,
    inputRef,
    handleIconClick,
    contextMenuAnchor,
    handleContextMenuClose,
    handleOptionSelect,
    selectedOption,
    popoverAnchor,
    handlePopoverClose,
    handleApplyResult,
    selectedText,
  ])

  return {
    renderAiComponents,
  }
}
